/**
 * /reset-password?token=… — set a new password via a one-time link.
 *
 * The token is consumed by the API atomically — if the user submits twice
 * the second submit will fail. The backend also revokes all existing
 * sessions on success, so the user has to log in again with the new
 * password (intentional: defends against a stolen cookie).
 */

import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";

import { AuthShell } from "../components/AuthShell";
import { PasswordInput } from "../components/PasswordInput";
import { Button, Field, Stack } from "../components/ui";
import { t } from "../i18n";
import { resetPasswordSchema } from "../lib/schemas";
import { trpc } from "../lib/trpc";
import { fieldError } from "../lib/validation";

interface ResetPasswordSearch {
  token?: string | undefined;
}

export const Route = createFileRoute("/reset-password")({
  validateSearch: (raw: Record<string, unknown>): ResetPasswordSearch => ({
    token: typeof raw.token === "string" ? raw.token : undefined,
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const reset = trpc.auth.resetPassword.useMutation();

  const form = useForm({
    defaultValues: { password: "", confirm: "" },
    validators: {
      onChange: resetPasswordSchema,
      onSubmit: resetPasswordSchema,
    },
    onSubmit: ({ value }) => {
      if (!search.token) return;
      reset.mutate({ token: search.token, newPassword: value.password });
    },
  });

  // After success, send the user back to login. We don't auto-log-in
  // because the API revokes the session bag — login with the new password
  // is the cleanest reset.
  useEffect(() => {
    if (!reset.data?.ok) return;
    const id = window.setTimeout(() => navigate({ to: "/" }), 2500);
    return () => window.clearTimeout(id);
  }, [reset.data?.ok, navigate]);

  if (!search.token) {
    return (
      <AuthShell title={t.resetPassword.title} description={t.resetPassword.invalidLink}>
        <Stack gap={3}>
          <Link
            to="/forgot-password"
            className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-fg text-sm hover:opacity-90"
          >
            {t.forgotPassword.submitButton}
          </Link>
        </Stack>
      </AuthShell>
    );
  }

  if (reset.data?.ok) {
    return (
      <AuthShell
        title={t.resetPassword.successTitle}
        description={t.resetPassword.successDescription}
      >
        <Stack gap={3}>
          <Link
            to="/"
            className="inline-flex h-11 items-center justify-center rounded-md bg-primary px-4 font-medium text-primary-fg text-sm hover:opacity-90"
          >
            {t.login.backToLogin}
          </Link>
        </Stack>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t.resetPassword.title} description={t.resetPassword.description}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <form.Field name="password">
          {(field) => (
            <Field
              label={t.resetPassword.passwordLabel}
              hint={t.resetPassword.passwordHint}
              error={fieldError(field)}
            >
              <PasswordInput
                autoComplete="new-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={reset.isPending}
                invalid={!!fieldError(field)}
                autoFocus
              />
            </Field>
          )}
        </form.Field>
        <form.Field name="confirm">
          {(field) => (
            <Field label={t.resetPassword.passwordConfirmLabel} error={fieldError(field)}>
              <PasswordInput
                autoComplete="new-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={reset.isPending}
                invalid={!!fieldError(field)}
              />
            </Field>
          )}
        </form.Field>
        <Button
          type="submit"
          variant="primary"
          size="lg"
          className="w-full"
          isLoading={reset.isPending}
          disabled={reset.isPending}
        >
          {reset.isPending ? t.resetPassword.submitting : t.resetPassword.submitButton}
        </Button>
        {reset.isError && (
          <p className="text-rose-600 text-xs dark:text-rose-400" role="alert">
            {reset.error.message}
          </p>
        )}
      </form>
    </AuthShell>
  );
}
